/**
 * ELF-CIRCLE — Team & Careers.
 *
 * Two jobs the overview asks of the same page: signal that the development team
 * is a real, growing bench rather than a single contractor (Section 8), and give
 * the About/Team narrative a credibility layer that enterprise procurement looks
 * for (Section 10).
 *
 * Deliberately role-shaped rather than people-shaped. Named profiles and
 * headshots are the client's to supply, and inventing them here would put
 * fictional colleagues on a real company's site — so the page renders the bench
 * as seats, marks which are filled and which are open, and leaves the names to
 * a later content pass.
 *
 * Signature mechanism: the seat map. Filtering by discipline re-deals the seats
 * rather than hiding rows, and an open seat expands in place into the role — so
 * the same object serves a prospect counting the bench and a candidate reading
 * the job.
 *
 * Imagery follows the same rule as the copy: drawn, role-shaped artwork — the
 * bench as seats, each discipline as its work, the advisory board as a table
 * still filling — never photographs standing in for colleagues who have not
 * been named. Static page — imagery is bundled rather than a CMS slot.
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import ElfFooter from '../ElfFooter';
import ElfPageNav, { ElfCrumb } from '../ElfPageNav';
import ElfShell from '../ElfShell';
import { useConsult } from '../elfConsult';
import { route } from '../../../../../shared/utils';
import advisoryArt from '../../../../../assets/elf/team-advisory.jpg';
import allArt from '../../../../../assets/elf/team-all.jpg';
import benchArt from '../../../../../assets/elf/team-bench.jpg';
import ciArt from '../../../../../assets/elf/team-ci.jpg';
import eventsArt from '../../../../../assets/elf/team-events.jpg';
import marketingArt from '../../../../../assets/elf/team-marketing.jpg';
import webArt from '../../../../../assets/elf/team-web.jpg';

type Discipline = 'Cultural Intelligence' | 'Marketing' | 'Web Development' | 'Events';

interface Seat {
  role: string;
  discipline: Discipline;
  /** `open` seats are hiring; `held` seats are filled today. */
  state: 'held' | 'open';
  /** Shown when an open seat is expanded. */
  detail?: string;
  /** Where the role sits — remote-first, but markets matter for events. */
  based?: string;
}

const SEATS: Seat[] = [
  { role: 'Principal & Founder', discipline: 'Cultural Intelligence', state: 'held' },
  { role: 'Cultural Intelligence Lead', discipline: 'Cultural Intelligence', state: 'held' },
  {
    role: 'Strategy Analyst',
    discipline: 'Cultural Intelligence',
    state: 'open',
    based: 'NY / NJ — hybrid',
    detail:
      'Turn the read into the document. You will sit between the rooms we are in and the plans that come out of them — audience research, signal tracking, and the translation step that hands a media plan and a build spec to two teams from one insight.',
  },
  { role: 'Media Buying & ROI', discipline: 'Marketing', state: 'held' },
  { role: 'Brand & Product Placement', discipline: 'Marketing', state: 'held' },
  { role: 'Social & Community', discipline: 'Marketing', state: 'held' },
  {
    role: 'SEO / GEO Strategist',
    discipline: 'Marketing',
    state: 'open',
    based: 'Remote — US',
    detail:
      'Own the connective layer. Generative Engine Optimization is treated as infrastructure here rather than an SEO add-on, which means working across both disciplines from one strategy — structured data and content architecture on the build side, authority and citation on the marketing side.',
  },
  { role: 'Lead Engineer', discipline: 'Web Development', state: 'held' },
  { role: 'Front-end Engineer', discipline: 'Web Development', state: 'held' },
  { role: 'Integrations Engineer', discipline: 'Web Development', state: 'held' },
  {
    role: 'Full-stack Engineer',
    discipline: 'Web Development',
    state: 'open',
    based: 'Remote — US',
    detail:
      'Build the middleware layer that keeps the platform from sprawling: one thin internal API between our front-ends and the scheduler, events, commerce, payments and CRM services, so a vendor swap never becomes a front-end rebuild.',
  },
  {
    role: 'Site Reliability / DevOps',
    discipline: 'Web Development',
    state: 'open',
    based: 'Remote — US',
    detail:
      'Take ownership of deploys, observability and uptime across client builds and our own properties. Small bench, real production traffic, and a direct line to the people writing the code.',
  },
  { role: 'Event Production Lead', discipline: 'Events', state: 'held' },
  {
    role: 'Market Producer — monthly series',
    discipline: 'Events',
    state: 'open',
    based: 'Rotating launch markets',
    detail:
      'Run the room. The monthly networking series is national by design, so this seat produces the same night in market after market — venue, programming, sponsors and the follow-through that makes the next one easier than the last.',
  },
];

/** The picture behind the seat map for each filter. */
const MAP_ART: Record<'All' | Discipline, string> = {
  All: allArt,
  'Cultural Intelligence': ciArt,
  Marketing: marketingArt,
  'Web Development': webArt,
  Events: eventsArt,
};

const FILTERS: ('All' | Discipline)[] = [
  'All',
  'Cultural Intelligence',
  'Marketing',
  'Web Development',
  'Events',
];

export const TeamCareers: React.FC = () => {
  const consult = useConsult();
  const [filter, setFilter] = useState<'All' | Discipline>('All');
  const [expanded, setExpanded] = useState<string | null>(null);

  const seats = useMemo(
    () => SEATS.filter((s) => filter === 'All' || s.discipline === filter),
    [filter],
  );
  const open = SEATS.filter((s) => s.state === 'open').length;

  return (
    <ElfShell>
      <div className="elf-page">
        <div className="elf-page__panel">
          <ElfPageNav current="/team" />
          <div style={{ padding: '18px var(--pgut) 0' }}>
            <ElfCrumb trail={[{ label: 'Home', to: '/' }, 'Team & Careers']} />
          </div>

          {/* --- the bench, counted --- */}
          <div className="team-head">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
              <span className="eyebrow" style={{ alignSelf: 'flex-start' }}>
                <i />
                A firm, not a freelancer
              </span>
              <h1 className="elf-page__h1">
                The bench is
                <br />
                <span style={{ color: 'var(--acc-panel)' }}>the differentiator.</span>
              </h1>
              <p className="wbody" style={{ maxWidth: '60ch' }}>
                A marketing firm with an in-house development team is a different proposition from a
                marketing firm that outsources the build. That only holds if the team is real — so
                here it is, seat by seat, including the ones still open.
              </p>
            </div>
            <div className="team-head__side">
              {/* Drawn to the page's own count: lit seats are held, outlined ones open. */}
              <img
                className="team-head__art"
                src={benchArt}
                alt={`A bench of ${SEATS.length} seats: ${SEATS.length - open} lit, ${open} outlined and still open`}
              />
              <div className="team-head__stats">
                {[
                  [String(SEATS.length), 'Seats on the bench'],
                  [String(SEATS.length - open), 'Held today'],
                  [String(open), 'Open now'],
                ].map(([n, l]) => (
                  <div className="statx" key={l}>
                    <span className="statn">{n}</span>
                    <span className="statl">{l}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* --- signature: the seat map --- */}
          <div className="team-map">
            {/* One picture per filter, all mounted; the current one is faded in. */}
            <span className="team-art" aria-hidden="true">
              {FILTERS.map((f) => (
                <img key={f} src={MAP_ART[f]} alt="" data-on={f === filter ? 'true' : undefined} />
              ))}
              <span />
            </span>
            <div className="team-map__head">
              <span className="colhead">The seat map</span>
              <div className="team-filters">
                {FILTERS.map((f) => (
                  <button
                    key={f}
                    type="button"
                    className={f === filter ? 'bfchip on' : 'bfchip'}
                    aria-pressed={f === filter}
                    onClick={() => {
                      setFilter(f);
                      setExpanded(null);
                    }}
                  >
                    {f}
                  </button>
                ))}
              </div>
            </div>

            <div className="team-seats">
              {seats.map((seat) => {
                const isOpen = seat.state === 'open';
                const on = expanded === seat.role;
                return (
                  <button
                    key={seat.role}
                    type="button"
                    className="team-seat"
                    data-state={seat.state}
                    data-on={on ? 'true' : undefined}
                    aria-expanded={isOpen ? on : undefined}
                    disabled={!isOpen}
                    onClick={() => setExpanded(on ? null : seat.role)}
                  >
                    <span className="team-seat__top">
                      <span className="statl">{seat.discipline}</span>
                      <span className="team-seat__flag">{isOpen ? 'Open' : 'Held'}</span>
                    </span>
                    <span className="whead" style={{ fontSize: 18 }}>
                      {seat.role}
                    </span>
                    {seat.based && <span className="wtag" style={{ fontSize: 12.5 }}>{seat.based}</span>}
                    {isOpen && (
                      <span className="team-seat__body" data-on={on ? 'true' : undefined}>
                        {/* One grid child, so `grid-template-rows: 0fr` actually
                            collapses — a second child lands in an implicit auto
                            row and stays visible while closed. */}
                        <span className="team-seat__inner">
                        <span className="wbody" style={{ fontSize: 14.5 }}>
                          {seat.detail}
                        </span>
                        <span
                          className="pillcta mag team-seat__apply"
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            consult?.open({
                              source: `careers.apply.${seat.role.toLowerCase().replace(/[^a-z]+/g, '-')}`,
                              title: `Apply — ${seat.role}`,
                            });
                          }}
                          onKeyDown={(e) => {
                            if (e.key !== 'Enter' && e.key !== ' ') return;
                            e.preventDefault();
                            e.stopPropagation();
                            consult?.open({
                              source: `careers.apply.${seat.role.toLowerCase().replace(/[^a-z]+/g, '-')}`,
                              title: `Apply — ${seat.role}`,
                            });
                          }}
                        >
                          Apply for this seat <span>→</span>
                        </span>
                        </span>
                      </span>
                    )}
                    {isOpen && !on && <span className="team-seat__hint">Read the role →</span>}
                  </button>
                );
              })}
            </div>
          </div>

          {/* --- advisory --- */}
          <div className="team-adv">
            <span className="team-art team-art--side" aria-hidden="true">
              <img src={advisoryArt} alt="" data-on="true" />
              <span />
            </span>
            <div>
              <span className="colhead">Credibility layer</span>
              <h2 className="elf-page__h2" style={{ marginTop: 10 }}>
                Advisory board — in formation.
              </h2>
              <p className="wbody" style={{ maxWidth: '56ch', marginTop: 14 }}>
                Top-tier competitors often win an RFP partly on who else is in the room. ELF-CIRCLE
                is recruiting an advisory bench across the three categories — entertainment,
                lifestyle and fashion — alongside institutional and municipal partners from the Made
                In New Jersey footprint. Names are announced when they are signed, not before.
              </p>
            </div>
            <button
              type="button"
              className="nextlink"
              onClick={() =>
                consult?.open({ source: 'careers.advisory', title: 'Advisory board conversation' })
              }
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">By introduction</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Join the advisory bench
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Entertainment · Lifestyle · Fashion.
                </span>
              </div>
              <span className="arw">→</span>
            </button>
          </div>

          <div
            className="elf-page__grid2"
            style={{
              padding: '0 var(--pgut) 72px',
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              borderTop: '1px solid rgba(var(--panel-fg-rgb),.1)',
            }}
          >
            <Link className="nextlink" to={route('/our-story')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">How we got here</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Our Story
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  From the ground up, into the firm.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
            <Link className="nextlink" to={route('/services')}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                <span className="statl">What the bench builds</span>
                <span className="whead" style={{ fontSize: 20 }}>
                  Two disciplines, one firm
                </span>
                <span className="wtag" style={{ fontSize: 12.5 }}>
                  Marketing and web development, in house.
                </span>
              </div>
              <span className="arw">→</span>
            </Link>
          </div>
        </div>
      </div>
      <ElfFooter />
    </ElfShell>
  );
};

export default TeamCareers;
